import React, { useState, useEffect } from "react";
import { DropdownToggle, DropdownMenu, UncontrolledDropdown } from "reactstrap";
import { useHistory } from "react-router-dom";
import Icon from "../../../../components/icon/Icon";
import { api } from "../../../../utils/api";
import { toast } from "react-toastify";

const NotificationItem = (props) => {
  const { icon, iconStyle, text, time, id, read_at, onMarkAsRead, onDelete, deepLink, onOpenDeepLink } = props;
  const isUnread = !read_at;

  const handleRowClick = () => {
    if (deepLink && onOpenDeepLink) {
      onOpenDeepLink(deepLink);
    }
  };

  return (
    <div
      className={`nk-notification-item ${isUnread ? 'nk-notification-unread' : ''}`}
      key={id}
      id={id}
      onClick={deepLink ? handleRowClick : undefined}
      style={deepLink ? { cursor: "pointer" } : undefined}
      role={deepLink ? "button" : undefined}
    >
      <div className="nk-notification-icon">
        <Icon name={icon} className={[`icon-circle ${iconStyle ? " " + iconStyle : ""}`]} />
      </div>
      <div className="nk-notification-content">
        <div className="nk-notification-text">{text}</div>
        <div className="nk-notification-time">{time}</div>
      </div>
      {isUnread && onMarkAsRead && (
        <button
          className="nk-notification-action"
          onClick={(e) => {
            e.stopPropagation();
            onMarkAsRead(id);
          }}
          title="Marquer comme lu"
        >
          <Icon name="check" />
        </button>
      )}
      {onDelete && (
        <button
          className="nk-notification-action text-danger"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(id);
          }}
          title="Supprimer"
        >
          <Icon name="trash" />
        </button>
      )}
    </div>
  );
};

const Notification = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const history = useHistory();

  useEffect(() => {
    loadNotifications();
    loadUnreadCount();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(() => {
      loadNotifications();
      loadUnreadCount();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await api.getNotifications({ per_page: 10, status: 'all' });
      // Laravel pagination structure: { data: [], total: number, ... }
      setNotifications(response.data || response || []);
    } catch (error) {
      console.error("Erreur lors du chargement des notifications:", error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await api.getUnreadNotificationsCount();
      setUnreadCount(response.count || 0);
    } catch (error) {
      console.error("Erreur lors du chargement du nombre de notifications non lues:", error);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await api.markNotificationAsRead(id);
      await loadNotifications();
      await loadUnreadCount();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour de la notification");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.markAllNotificationsAsRead();
      await loadNotifications();
      await loadUnreadCount();
      toast.success("Toutes les notifications ont été marquées comme lues");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour des notifications");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteNotification(id);
      await loadNotifications();
      await loadUnreadCount();
      toast.success("Notification supprimée");
    } catch (error) {
      toast.error("Erreur lors de la suppression de la notification");
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  };

  return (
    <UncontrolledDropdown className="user-dropdown">
      <DropdownToggle tag="a" className="dropdown-toggle nk-quick-nav-icon">
        <div className={`icon-status ${unreadCount > 0 ? "icon-status-info" : ""}`}>
          <Icon name="bell" style={{ color: "#fff" }} />
          {unreadCount > 0 && <span className="badge badge-sm badge-dot badge-danger"></span>}
        </div>
      </DropdownToggle>
      <DropdownMenu end className="dropdown-menu-xl dropdown-menu-s1">
        <div className="dropdown-head">
          <span className="sub-title nk-dropdown-title">Notifications</span>
          {unreadCount > 0 && (
            <a
              href="#markasread"
              onClick={(ev) => {
                ev.preventDefault();
                handleMarkAllAsRead();
              }}
            >
              Tout marquer comme lu
            </a>
          )}
        </div>
        <div className="dropdown-body">
          <div className="nk-notification">
            {loading ? (
              <div className="text-center p-3">
                <span className="text-soft">Chargement...</span>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center p-3">
                <span className="text-soft">Aucune notification</span>
              </div>
            ) : (
              notifications.map((item) => {
                const deep =
                  item.deep_link ||
                  item.data?.deep_link ||
                  (item.data?.observation_id ? `/observations/${item.data.observation_id}/view` : null);
                return (
                  <NotificationItem
                    key={item.id}
                    id={item.id}
                    icon={item.icon}
                    iconStyle={item.iconStyle}
                    text={item.message || item.title}
                    time={formatTime(item.created_at)}
                    read_at={item.read_at}
                    deepLink={deep}
                    onOpenDeepLink={(path) => history.push(`${process.env.PUBLIC_URL || ""}${path}`)}
                    onMarkAsRead={handleMarkAsRead}
                    onDelete={handleDelete}
                  />
                );
              })
            )}
          </div>
        </div>
        <div className="dropdown-foot center">
          <a
            href="#viewall"
            onClick={(ev) => {
              ev.preventDefault();
              history.push(`${process.env.PUBLIC_URL}/notifications`);
            }}
          >
            Voir tout
          </a>
        </div>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

export default Notification;
